export const UX_JOURNEY_STORAGE_KEY = 'ux_journey_v1';
export const UX_PAGE_SEQUENCE_MAX = 20;

export type ConversionType = 'order' | 'proposal';

export type OrderSource = 'direct' | 'proposal';

export type FunnelStep = 'cart' | 'shipping' | 'payment' | 'confirm';

export type AbandonmentReason = 'tab_hidden' | 'session_exit';

export type UxJourneyMilestones = {
  first_cart_with_items_at?: number;
  checkout_shipping_at?: number;
  checkout_payment_at?: number;
  conversion_at?: number;
};

export type UxCartSnapshot = {
  cart_id: string;
  item_count: number;
  total: number;
  has_items: boolean;
  cart_type?: string;
  category?: string;
  proposal_code?: number;
  from_proposal?: boolean;
};

export type UxLastRoute = {
  path: string;
  name?: string | null;
  entered_at: number;
};

export type UxJourneyState = {
  journey_id: string;
  session_started_at: number;
  milestones: UxJourneyMilestones;
  conversion_type?: ConversionType;
  last_route: UxLastRoute;
  cart_snapshot?: UxCartSnapshot;
  page_sequence: string[];
  cart_visit_count: number;
};

export type UxJourneyMetrics = {
  time_from_session_start_ms: number;
  time_from_first_cart_ms?: number;
  time_in_checkout_ms?: number;
};

export type UxJourneyContext = {
  journey_id: string;
  partner_id?: number;
  is_personification?: boolean;
  region?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  funnel_step?: FunnelStep | null;
  route_path?: string;
};

export type UxEventPayload = {
  event: string;
  timestamp: number;
  journey_id: string;
  metrics?: UxJourneyMetrics;
  context?: Record<string, unknown>;
  [key: string]: unknown;
};

export type RecordConversionInput = {
  type: ConversionType;
  cart_id?: string;
  order_id?: number;
  /** Código do orçamento salvo (conversão tipo `proposal`). */
  proposal_code?: number;
  payment_method?: string;
  /** Origem do pedido fechado (conversão tipo `order`). */
  order_source?: OrderSource;
  /** Código do orçamento de origem, quando `order_source` é `proposal`. */
  source_proposal_code?: number;
};

export type OrderSourceResolution = {
  order_source: OrderSource;
  source_proposal_code?: number;
};

type CartWithProposalHint = {
  type?: { description?: string | null } | null;
  proposal?: { code?: number | null } | null;
};

/** Identifica se o pedido foi fechado a partir de um carrinho/orçamento existente. */
export function resolveOrderSourceFromCart(
  cart: CartWithProposalHint | null | undefined,
): OrderSourceResolution {
  const proposalCode = cart?.proposal?.code ?? undefined;
  const cartTypeIsProposal = cart?.type?.description === 'proposal';

  if (proposalCode != null || cartTypeIsProposal) {
    return {
      order_source: 'proposal',
      ...(proposalCode != null ? { source_proposal_code: proposalCode } : {}),
    };
  }

  return { order_source: 'direct' };
}

export const UX_JOURNEY_EVENTS = {
  SESSION_START: 'ux_session_start',
  NAVIGATION: 'ux_navigation',
  FUNNEL_ENTER: 'ux_funnel_enter',
  SESSION_PAUSE: 'ux_session_pause',
  SESSION_RESUME: 'ux_session_resume',
  SESSION_EXIT: 'ux_session_exit',
  CART_ABANDONMENT: 'ux_cart_abandonment_signal',
  CONVERSION_COMPLETED: 'ux_conversion_completed',
} as const;

export type UxJourneyEventName =
  (typeof UX_JOURNEY_EVENTS)[keyof typeof UX_JOURNEY_EVENTS];

export function getFunnelStepFromPath(path: string): FunnelStep | null {
  if (path.startsWith('/checkout/confirm')) return 'confirm';
  if (path.startsWith('/checkout/payment')) return 'payment';
  if (path.startsWith('/checkout/shipping')) return 'shipping';
  if (path.startsWith('/cart')) return 'cart';
  return null;
}

export function isFunnelRoute(path: string): boolean {
  return getFunnelStepFromPath(path) !== null;
}
